/**
 * <s-bundle-picker> — Bundle quantity cards for the product form.
 *
 * Each card is a radio input with a `value` holding the bundle quantity. The
 * radios have no `name`, so they are never submitted; picking one writes its
 * quantity into the form's authoritative quantity input and fires `input` and
 * `change` so the quantity selector stays in sync. Editing the quantity input
 * by hand selects the matching card, or clears the selection when none match.
 *
 * Required child markup:
 *   input[type="radio"][data-bundle-quantity] — one per bundle option.
 */
class SBundlePicker extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('change', (event) => {
      const radio = event.target;
      if (!(radio instanceof HTMLInputElement) || !radio.matches('[data-bundle-quantity]')) return;

      const quantityInput = this.#quantityInput();
      if (!quantityInput) return;

      quantityInput.value = radio.dataset.bundleQuantity;
      quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }, opts);

    const form = this.closest('form');
    form?.addEventListener('change', (event) => this.#syncFromQuantity(event), opts);
    form?.addEventListener('s-quantity:change', () => this.#syncFromQuantity(), opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #quantityInput() {
    const form = this.closest('form');
    const input = form?.elements.namedItem('quantity');
    return input instanceof HTMLInputElement ? input : null;
  }

  #syncFromQuantity(event) {
    if (event && this.contains(event.target)) return;

    const quantityInput = this.#quantityInput();
    if (!quantityInput) return;

    const value = String(Number.parseInt(quantityInput.value, 10) || 1);
    this.querySelectorAll('[data-bundle-quantity]').forEach((radio) => {
      radio.checked = radio.dataset.bundleQuantity === value;
    });
  }
}

if (!customElements.get('s-bundle-picker')) {
  customElements.define('s-bundle-picker', SBundlePicker);
}
